import React, { FC, useContext } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import styles from './styles.module.css';

import {
  PolicyTermInfoType,
  EffectivePeriodType,
  TermStatusDetailType,
  GetPaymentsSummaryByTermNumberResponse,
} from '@clearcover/core-app-types';
import { Button, Tooltip, Asset, PaymentsIcon } from '@clearcover/atlas';

import { PolicySystemOfRecordIndicator } from '../PolicySystemOfRecordIndicator';
import { PolicyStatus, PolicySubStatusSection } from '../PolicyStatus';

import { useAuth0 } from 'Auth';
import { env } from 'Shared/constants/env';
import { formatDateTimeMiddleUsingTimezone } from 'Shared/utils';
import { PolicyContext } from 'Policies/utils';
import { PolicyTermChooser } from '../PolicyTermChooser';
import { CopyContentButton } from 'Shared/components/CopyContentButton';
import { PendingDraft } from '../PendingDraft';
import { useTitle } from 'Shared/hooks';
import classNames from 'classnames';
import { UnsignedPacketsAlert } from '../UnsignedPacketsAlert';
import { PolicyProductSegmentIndicator } from '../PolicyProductSegmentIndicator/PolicyProductSegmentIndicator';

export type PolicyDetailsHeaderType = {
  applicantName: string;
  effectivePeriod: EffectivePeriodType;
  policyNumber: string;
  termInfo: PolicyTermInfoType;
  currentPolicyTermNumber?: number;
  buttons?: React.ReactElement;
  renewalDraftId?: string;
  processOne: boolean;
  termStatusDetailsList?: Array<TermStatusDetailType>;
  draftList: Array<{
    id: string;
    type:
      | 'mid_term'
      | 'renewal'
      | 'renewal_quote'
      | 'cancellation'
      | 'reinstatement';
    effectivePeriod: {
      startAt: string;
      endAt: string;
    };
  }>;
  // productSegment only needs to be passed in if not present in the PolicyContext
  productSegment?: string;
  billingInfoResponse?: GetPaymentsSummaryByTermNumberResponse;
  ignoreBanners?: Array<TermStatusDetailType>;
  showTermChooser?: boolean;
};

const oneIncLinkMap: { one_inc_3: string; one_inc_4: string } = {
  one_inc_3: env.oneInc3ExternalLink,
  one_inc_4: env.oneInc4ExternalLink,
};

export const PolicyDetailsHeader: FC<PolicyDetailsHeaderType> = ({
  applicantName,
  policyNumber,
  effectivePeriod,
  termInfo,
  currentPolicyTermNumber,
  buttons,
  renewalDraftId,
  processOne,
  draftList = [],
  termStatusDetailsList = [],
  productSegment,
  billingInfoResponse,
  ignoreBanners,
  showTermChooser = true,
}) => {
  const { user } = useAuth0();
  const history = useHistory();
  const {
    systemOfRecord,
    details: { timeZone, draftType },
    loading,
    productSegment: contextProductSegment,
  } = useContext(PolicyContext);
  const headerProductSegment = productSegment || contextProductSegment;
  const { id: policyId } = useParams<{
    id: string;
    draftId: string;
  }>();
  useTitle(`${policyNumber} - ${applicantName}`);
  const policyIsPendingCancellation = termStatusDetailsList.some((status) =>
    ['pending_cancellation_other', 'pending_cancellation_non_payment'].includes(
      status
    )
  );

  const loaded = !!termInfo && !loading;
  const canViewOpenInOneIncButton =
    ('one_inc_3' === systemOfRecord || 'one_inc_4' === systemOfRecord) &&
    (user!.isCarrierUser || user!.isSystemUser);

  return (
    <div
      data-testid={
        loaded ? 'policy-details-header' : 'policy-details-header-loading'
      }
      className={classNames(styles.container)}
    >
      <h1
        className={classNames('fs-exclude', styles.header, {
          [styles.loading]: loading,
        })}
      >
        {applicantName}
      </h1>
      <div className={styles.buttonGroup}>{buttons}</div>
      <div className={styles.statusLine}>
        <div data-testid="policy-header-number" className={styles.policyNumber}>
          Policy Number:
          <span
            data-testid="policy-header-number-value"
            className={classNames({ [styles.loading]: loading })}
          >
            #{policyNumber ?? 'IL123456'}
          </span>
          <div className={styles.copyButton}>
            <CopyContentButton value={policyNumber} />
          </div>
        </div>

        {systemOfRecord && systemOfRecord !== 'missing' && (
          <PolicySystemOfRecordIndicator systemOfRecord={systemOfRecord} />
        )}

        {headerProductSegment && (
          <PolicyProductSegmentIndicator
            productSegment={headerProductSegment}
          />
        )}

        {canViewOpenInOneIncButton && (
          <a
            href={`${oneIncLinkMap[systemOfRecord]}/Html/policy/term/${termInfo.termId}/home`}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.oneIncLink}
          >
            <Button size="sm">Open in OneInc</Button>
          </a>
        )}
        {processOne && (
          <Tooltip
            position="top"
            tooltip={`The payment processor for this policy is Process One`}
          >
            <span
              className={styles.processOneIndicator}
              data-testid="policy-header-processone"
            >
              <Asset
                icon={PaymentsIcon}
                className={styles.processOneIndicatorIcon}
                description="Process One"
                height={12}
                width={14}
              />
              Process One
            </span>
          </Tooltip>
        )}
        <span>
          {showTermChooser && (
            <PolicyTermChooser
              policyNumber={policyNumber}
              currentTermNumber={currentPolicyTermNumber ?? 0}
              onTermChange={(termId) => {
                history.push(`/policies/${policyId}/term/${termId}`);
              }}
              timeZone={timeZone}
            />
          )}
        </span>
        <PolicyStatus
          status={
            // @ts-expect-error TS is not accepting that draftType can be undefined
            ['renewal', 'renewal_quote'].includes(draftType)
              ? 'renewal'
              : termInfo?.termStatus
          }
        />
        <div data-testid="effective-date" className={styles.effectiveDate}>
          Effective:
          <span
            className={classNames(styles.effective, {
              [styles.loading]: !loaded,
            })}
          >{`${formatDateTimeMiddleUsingTimezone(
            effectivePeriod?.startsAt ?? '2016-11-01',
            timeZone
          )} - ${formatDateTimeMiddleUsingTimezone(
            effectivePeriod?.endsAt ?? '2016-11-01',
            timeZone
          )}`}</span>
          <div className={styles.copyButton}>
            <CopyContentButton
              value={`${formatDateTimeMiddleUsingTimezone(
                effectivePeriod?.startsAt ?? '2016-11-01',
                timeZone
              )} - ${formatDateTimeMiddleUsingTimezone(
                effectivePeriod?.endsAt ?? '2016-11-01',
                timeZone
              )}`}
            />
          </div>
        </div>
      </div>
      <PolicySubStatusSection
        policyId={policyId}
        termInfo={termInfo}
        effectivePeriod={effectivePeriod}
        timeZone={timeZone}
        draftType={draftType}
        renewalDraftId={renewalDraftId}
        billingInfoResponse={billingInfoResponse}
        ignoreBanners={ignoreBanners}
      />
      {!policyIsPendingCancellation &&
        draftList.map((draft, index) => (
          <PendingDraft
            key={index}
            id={draft.id}
            type={draft.type}
            effectivePeriod={draft.effectivePeriod}
          />
        ))}
      {/* {<FutureCancellationAlert appliedCancellationEffectiveDate={} />} */}

      <UnsignedPacketsAlert />
    </div>
  );
};
